import { ModalViewModel } from '@/infra/ModalViewModel'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { action, observable, runInAction } from 'mobx'
import { User } from '@/features/iam/users/User'
import type { UserTransactionReason } from '@/features/payouts/user-transaction-reason'

/**
 * Details of the transaction to confirm.
 */
export interface PromptConfirmUserTransactionOptions {
  amount: number
  recipient: User
  reason: UserTransactionReason | null
  signal?: AbortSignal
}

/**
 * Confirmation for creating high value transactions.
 */
export class ConfirmUserTransactionDialogViewModel {
  readonly modal = new ModalViewModel()

  private readonly _prompt = new AsyncPrompt<boolean>({
    abort$: this.modal.close$,
  })

  @observable accessor amount = 0

  @observable accessor recipient = User.none

  @observable accessor reason: UserTransactionReason | null = null

  /**
   * Asks the user to confirm the transaction.
   *
   * @returns whether the user confirmed.
   */
  async prompt({
    amount,
    recipient,
    reason,
    signal,
  }: PromptConfirmUserTransactionOptions) {
    this.modal.close()
    const responsePromise = this._prompt.prompt(signal)
    runInAction(() => {
      this.amount = amount
      this.recipient = recipient
      this.reason = reason
      this.modal.show()
    })
    const response = await responsePromise
    this.modal.close()
    return response ?? false
  }

  /**
   * Confirms the transaction.
   */
  @action.bound
  confirm() {
    this._prompt.respond(true)
  }
}
